import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { Discount, LineItem } from 'shared/types/cart';
import { Product, Variant } from 'shared/types/product';

export interface ItemDataLayer {
  item_id?: string;
  item_name?: string;
  item_list_name?: string;
  item_list_id?: string;
  index?: number;
  coupon?: string | null;
  discount?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  sale_status?: boolean;
  stock_status?: boolean;
  actual_size?: string;
  original_price?: number;
  price?: number;
  quantity?: number;
  item_image: string;
  item_url: string;
  promotion_id?: string;
  promotion_name?: string;
}

export interface CJProductLayer {
  productTitle?: string;
  productType?: string;
  productSku?: string;
  productPrice?: number;
  productQuantity?: number;
  productDiscount?: number;
}
export class LineItemMapper {
  static fromLineItemToItemDataLayer(
    lineItem: LineItem,
    quantity: number,
    index?: number,
    discounts?: Discount[],
  ): ItemDataLayer {
    const originalPrice = lineItem.variant?.originalPrice
      ? CurrencyHelpers.formatAmount(lineItem.variant.originalPrice)
      : null;
    const discountedPrice: number = lineItem.discountedPrice
      ? CurrencyHelpers.formatAmount(lineItem.discountedPrice)
      : 0;

    const variantPrice: number = CurrencyHelpers.formatAmount(lineItem.variant?.price!);
    const discount =
      discountedPrice > 0
        ? originalPrice
          ? parseFloat((originalPrice - discountedPrice).toFixed(2))
          : parseFloat((variantPrice - discountedPrice).toFixed(2))
        : originalPrice
          ? parseFloat((originalPrice - variantPrice).toFixed(2))
          : 0;

    return {
      item_id: lineItem.variant?.sku?.replace(/[^a-zA-Z0-9]/g, '') || '',
      item_name: lineItem.name || '',
      discount: parseFloat((discount * quantity).toFixed(2)),
      coupon: discounts?.length ? discounts[0].code : null,
      item_category: lineItem.variant?.attributes?.categories?.[0],
      item_category2: lineItem.variant?.attributes?.categories?.[1] || '',
      item_variant: lineItem.variant?.attributes?.actualColor?.trim(),
      item_image: lineItem.variant?.images?.[0] || '',
      item_url: lineItem._url || '',
      index,
      actual_size: lineItem.variant?.attributes?.actualSize?.trim(),
      original_price: discountedPrice > 0 ? originalPrice || variantPrice : originalPrice || null,
      sale_status: !!lineItem.variant?.originalPrice || !!lineItem.discountedPrice,
      stock_status: true,
      price: discountedPrice ? discountedPrice : variantPrice,
      quantity: quantity,
    };
  }

  static fromProductToItemDataLayer(product: Product, quantity: number, selectedVariant: Variant): ItemDataLayer {
    const originalPrice = selectedVariant?.originalPrice
      ? CurrencyHelpers.formatAmount(selectedVariant.originalPrice)
      : null;
    const discountedPrice: number = selectedVariant.discountedPrice
      ? CurrencyHelpers.formatAmount(selectedVariant.discountedPrice)
      : 0;

    const variantPrice: number = CurrencyHelpers.formatAmount(selectedVariant.price!);
    const discount =
      discountedPrice > 0
        ? originalPrice
          ? parseFloat((originalPrice - discountedPrice).toFixed(2))
          : parseFloat((variantPrice - discountedPrice).toFixed(2))
        : originalPrice
          ? parseFloat((originalPrice - variantPrice).toFixed(2))
          : 0;
    return {
      item_id: selectedVariant.sku.replace(/[^a-zA-Z0-9]/g, ''),
      item_name: product.name,
      discount: parseFloat((discount * quantity).toFixed(2)),
      item_category: selectedVariant.attributes?.categories?.[0],
      item_category2: selectedVariant.attributes?.categories?.[1],
      item_variant: selectedVariant.attributes?.actualColor?.trim(),
      item_image: selectedVariant?.images?.[0] || '',
      item_url: product._url || '',
      actual_size: selectedVariant?.attributes?.actualSize?.trim(),
      original_price: discountedPrice > 0 ? originalPrice || variantPrice : originalPrice || null,
      sale_status: !!selectedVariant.originalPrice || !!selectedVariant.discountedPrice,
      stock_status: true,
      price: discountedPrice ? discountedPrice : variantPrice,
      quantity,
    };
  }

  static fromLineItemToCJProductLayer(lineItem: LineItem): CJProductLayer {
    const discountedPrice: number = lineItem.discountedPrice
      ? CurrencyHelpers.formatAmount(lineItem.discountedPrice)
      : 0;

    const variantPrice: number = CurrencyHelpers.formatAmount(lineItem.variant?.price!);
    let totalDiscounts = 0;
    lineItem.discountedPricePerCount && lineItem.discountedPricePerCount?.length > 0
      ? lineItem.discountedPricePerCount?.[0].discounts?.forEach((discount) => {
        totalDiscounts += discount.discountedAmount?.centAmount ?? 0;
      })
      : 0;

    return {
      productTitle: lineItem?.name,
      productType: lineItem?.productType,
      productSku: lineItem?.variant?.sku.replace(/[^a-zA-Z0-9]/g, ''),
      productPrice: discountedPrice || variantPrice,
      productQuantity: lineItem?.count,
      productDiscount: CurrencyHelpers.formatAmount({
        centAmount: totalDiscounts,
        currencyCode: 'USD',
        fractionDigits: 2,
      }),
    };
  }
}
